/**
 * Client-side calendar helpers.
 *
 * Dashplan stores a day as a `YYYY-MM-DD` key and a time as minutes from local
 * midnight. The browser is the only place that knows the user's real wall
 * clock, so every conversion between an instant and those two values happens
 * here rather than on the server.
 */

/** Today's key in the browser's timezone. */
export function todayKey(at: Date = new Date()): string {
  return dateKey(at);
}

/**
 * `YYYY-MM-DD` for a Date, in local time.
 *
 * Built from the local getters rather than `toISOString().slice(0,10)`, which
 * converts to UTC first and so returns the wrong day near a midnight boundary:
 * 00:30 in Oslo (UTC+2) is still the previous day in UTC, and 23:30 in New
 * York (UTC-4) is already the next one.
 */
export function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a `YYYY-MM-DD` key into a local-midnight Date. */
export function keyToDate(key: string): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Shift a day key by whole days. */
export function addDays(key: string, days: number): string {
  const d = keyToDate(key);
  d.setDate(d.getDate() + days);
  return dateKey(d);
}

/** Minutes from local midnight, 0-1439. */
export function minuteOfDay(d: Date = new Date()): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** `840` → `"14:00"`. */
export function formatMinute(minute: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.round(minute)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * The same, for the *end* of something: 1440 is midnight, not 23:59.
 *
 * `formatMinute` clamps to 23:59 because no block may start at midnight. An
 * end may, and a block resized to the end of the day reading "23:59" next to
 * a duration that says otherwise is a visible off-by-one.
 */
export function formatEndMinute(minute: number): string {
  return minute >= 1440 ? "24:00" : formatMinute(minute);
}

/** `"14:00"` or `"14.00"` or `"1400"` → 840. Returns null if unparseable. */
export function parseMinute(input: string): number | null {
  const trimmed = input.trim();
  const match = trimmed.match(/^(\d{1,2})[:.\s]?(\d{2})$/);
  if (!match) return null;
  const h = Number(match[1]);
  const m = Number(match[2]);
  if (h > 23 || m > 59) return null;
  return h * 60 + m;
}

/** `90` → `"1h 30m"`, `45` → `"45m"`, `120` → `"2h"`. */
export function formatDuration(minutes: number): string {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/** 0=Sunday .. 6=Saturday, derived from the key alone. */
export function dayOfWeek(key: string): number {
  return keyToDate(key).getDay();
}

const WEEKDAY_LONG = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const DAY_LABEL = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  day: "numeric",
  month: "long",
});

/** "Monday 22 September", or "Today" / "Tomorrow" / "Yesterday" when close. */
export function formatDayLabel(key: string, today = todayKey()): string {
  if (key === today) return "Today";
  if (key === addDays(today, 1)) return "Tomorrow";
  if (key === addDays(today, -1)) return "Yesterday";
  return DAY_LABEL.format(keyToDate(key));
}

export function weekdayName(key: string): string {
  return WEEKDAY_LONG.format(keyToDate(key));
}

/** The seven day keys of the week containing `key`, Monday first. */
export function weekOf(key: string): string[] {
  const d = keyToDate(key);
  // getDay() is Sunday-based; shift so Monday is the first column.
  const offset = (d.getDay() + 6) % 7;
  const monday = addDays(key, -offset);
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

/** The browser's IANA timezone, e.g. "Europe/Oslo". */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Round a minute value to the nearest `step`, staying inside one day.
 *
 * The upper bound is the largest multiple of `step` that still fits in the day
 * (1435 for a 5-minute step), not 1439. Clamping to 1439 after snapping would
 * return an off-grid value from a function whose whole purpose is to return an
 * on-grid one — so a block dragged to the very bottom of the timeline would
 * land at 23:59 instead of 23:55.
 */
export function snapMinute(minute: number, step = 5): number {
  const snapped = Math.round(minute / step) * step;
  const lastOnGrid = Math.floor(1439 / step) * step;
  return Math.max(0, Math.min(lastOnGrid, snapped));
}
